import { useCallback, useRef, useState } from "react";
import type { Vec2 } from "@townsy/contracts";
import { joystickVector } from "./keyboard";

/**
 * Touch joystick covering the left half of the viewport in walk mode (docs/01 section 4:
 * "Touch uses a virtual movement control and drag-to-look"). Dragging elsewhere reaches the
 * canvas and orbits the follow camera. The control is pointer-based so it also works with a
 * mouse on touch laptops; keyboard users never see it. While driving (ADR-0006) the same control
 * is the pedal and wheel: up = gas, down = brake / reverse, sideways = steer.
 */
export const JOYSTICK_RADIUS_PX = 60;

export function joystickHint(driving: boolean): string {
  return driving ? "Touch here to drive: up gas · down brake/reverse · sideways steer" : "Touch here to walk · drag elsewhere to look";
}

export function Joystick({ onChange, driving = false }: { onChange(v: Vec2): void; driving?: boolean }) {
  const [base, setBase] = useState<{ x: number; y: number } | null>(null);
  const [knob, setKnob] = useState<{ x: number; y: number } | null>(null);
  const pointerId = useRef<number | null>(null);

  const end = useCallback(() => {
    pointerId.current = null;
    setBase(null);
    setKnob(null);
    onChange({ x: 0, z: 0 });
  }, [onChange]);

  return (
    <div
      className="joystick-zone"
      aria-hidden="true"
      onPointerDown={(e) => {
        if (pointerId.current !== null) return;
        pointerId.current = e.pointerId;
        e.currentTarget.setPointerCapture(e.pointerId);
        const rect = e.currentTarget.getBoundingClientRect();
        const p = { x: e.clientX - rect.left, y: e.clientY - rect.top };
        setBase(p);
        setKnob(p);
      }}
      onPointerMove={(e) => {
        if (e.pointerId !== pointerId.current || !base) return;
        const rect = e.currentTarget.getBoundingClientRect();
        const dx = e.clientX - rect.left - base.x;
        const dy = e.clientY - rect.top - base.y;
        const len = Math.hypot(dx, dy) || 1;
        const clamped = Math.min(len, JOYSTICK_RADIUS_PX);
        setKnob({ x: base.x + (dx / len) * clamped, y: base.y + (dy / len) * clamped });
        onChange(joystickVector(dx, dy, JOYSTICK_RADIUS_PX));
      }}
      onPointerUp={(e) => {
        if (e.pointerId === pointerId.current) end();
      }}
      onPointerCancel={(e) => {
        if (e.pointerId === pointerId.current) end();
      }}
      onLostPointerCapture={end}
    >
      {base && knob ? (
        <>
          <div className="joystick-base" style={{ left: base.x, top: base.y }} />
          <div className="joystick-knob" style={{ left: knob.x, top: knob.y }} />
        </>
      ) : (
        <div className="joystick-hint">{joystickHint(driving)}</div>
      )}
    </div>
  );
}
